import { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { PERFORMANCE_ERROR } from '../../hooks/useMemberPerformanceProfile';
import { Card, Section } from './Card';
import { AWAY_LABEL_MAX, AWAY_MAX_DAYS, COLORS } from './constants';
import { DAY_MS } from './weeks';

const DEFAULT_LABEL = 'Away';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const parseDate = (value) => {
  if (!DATE_RE.test(value || '')) {
    return null;
  }
  const time = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(time) || new Date(time).toISOString().slice(0, 10) !== value) {
    return null;
  }
  return time;
};

const isoDay = (time) => new Date(time).toISOString().slice(0, 10);

export const formatRange = (start, end) => (start === end ? start : `${start} to ${end}`);

// Inclusive date limits (UTC): 3 years back, 1 year ahead.
export const getDateLimits = (now = new Date()) => {
  const todayTime = Date.parse(`${isoDay(now.getTime())}T00:00:00Z`);
  const earliest = new Date(todayTime);
  earliest.setUTCFullYear(earliest.getUTCFullYear() - 3);
  const latest = new Date(todayTime);
  latest.setUTCFullYear(latest.getUTCFullYear() + 1);
  return { earliest: earliest.getTime(), latest: latest.getTime() };
};

// Returns { startDate?, endDate?, label? } field errors. Empty object means valid.
export const validateAwayPeriod = ({ startDate, endDate, label }, now = new Date()) => {
  const errors = {};
  const { earliest, latest } = getDateLimits(now);

  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!startDate) {
    errors.startDate = 'Choose a start date.';
  } else if (start === null) {
    errors.startDate = 'Enter a valid start date.';
  } else if (start < earliest) {
    errors.startDate = 'The start date cannot be more than 3 years ago.';
  }
  if (!endDate) {
    errors.endDate = 'Choose an end date.';
  } else if (end === null) {
    errors.endDate = 'Enter a valid end date.';
  } else if (end > latest) {
    errors.endDate = 'The end date cannot be more than 1 year ahead.';
  }
  if (!errors.startDate && !errors.endDate) {
    if (start > end) {
      errors.endDate = 'The end date must be on or after the start date.';
    } else if ((end - start) / DAY_MS + 1 > AWAY_MAX_DAYS) {
      errors.endDate = `An away period can be at most ${AWAY_MAX_DAYS} days.`;
    }
  }
  if (String(label ?? '').trim().length > AWAY_LABEL_MAX) {
    errors.label = `The label can be at most ${AWAY_LABEL_MAX} characters.`;
  }
  return errors;
};

const GENERIC_ERROR = 'Could not save your change. Please try again.';
const ALREADY_REMOVED = 'Already removed.';
const FIELD_ORDER = [
  ['startDate', 'away-start'],
  ['endDate', 'away-end'],
  ['label', 'away-label'],
];
const EMPTY_FORM = { startDate: '', endDate: '', label: DEFAULT_LABEL };

const AwayPeriods = ({ awayPeriods, onAdd, onDelete, onSignIn }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [listError, setListError] = useState('');
  const pendingFocus = useRef(null);
  const deleteButtons = useRef({});
  const yesButton = useRef(null);
  const listHeading = useRef(null);

  // Focus moves are queued by handlers and applied after the render that makes them possible.
  useEffect(() => {
    if (pendingFocus.current) {
      const target = pendingFocus.current();
      pendingFocus.current = null;
      if (target && target.focus) {
        target.focus();
      }
    }
  });

  const periods = Array.isArray(awayPeriods) ? awayPeriods : [];
  const userPeriods = periods.filter((p) => p?.kind === 'user');
  const inferred = periods.filter((p) => p?.kind === 'inferred');

  const setField = (name) => (event) => {
    const nextForm = { ...form, [name]: event.target.value };
    setForm(nextForm);
    setErrors((prev) => {
      const next = { ...prev, [name]: undefined };
      if (name !== 'label') {
        // Editing either date re-checks the other date's error, so a fixed pair clears at once.
        const fresh = validateAwayPeriod(nextForm);
        ['startDate', 'endDate'].forEach((field) => {
          if (field !== name && prev[field]) {
            next[field] = fresh[field];
          }
        });
      }
      return next;
    });
  };

  // The shared failure path: sign in, a server validation message, or a generic error.
  const handleFailure = (err, setMessage, notFound = false) => {
    if (err?.kind === PERFORMANCE_ERROR.SIGN_IN) {
      if (onSignIn) {
        onSignIn();
      }
      return;
    }
    setMessage(notFound ? ALREADY_REMOVED : err?.userMessage || GENERIC_ERROR);
  };

  // Every action starts clean, so only one alert (and one announcement) is ever current.
  const clearMessages = () => {
    setFormError('');
    setListError('');
    setNotice('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    const found = validateAwayPeriod(form);
    setErrors(found);
    clearMessages();
    const firstInvalid = FIELD_ORDER.find(([field]) => found[field]);
    if (firstInvalid) {
      document.getElementById(firstInvalid[1])?.focus();
      return;
    }
    setSaving(true);
    try {
      await onAdd({
        startDate: form.startDate,
        endDate: form.endDate,
        label: form.label.trim() || DEFAULT_LABEL,
      });
      setForm(EMPTY_FORM);
      setNotice('Away period added.');
    } catch (err) {
      handleFailure(err, setFormError);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (period) => {
    clearMessages();
    setDeletingId(period.id);
    try {
      await onDelete(period.id);
      pendingFocus.current = () => listHeading.current;
      setConfirmId(null);
      setNotice('Away period deleted.');
    } catch (err) {
      if (err?.kind !== PERFORMANCE_ERROR.SIGN_IN) {
        // The profile was refetched, so the row is gone or current. Close the confirm either way.
        pendingFocus.current = () => listHeading.current;
        setConfirmId(null);
      }
      handleFailure(err, setListError, err?.kind === PERFORMANCE_ERROR.NOT_FOUND);
    } finally {
      setDeletingId(null);
    }
  };

  const prefill = (period) => {
    // Keep a label the member already typed.
    setForm((prev) => ({
      startDate: period.startDate,
      endDate: period.endDate,
      label: prev.label.trim() ? prev.label : DEFAULT_LABEL,
    }));
    setErrors({});
    clearMessages();
    setNotice('Dates filled in below. Review them and choose Add away period.');
    const el = document.getElementById('away-start');
    if (el && el.focus) {
      el.focus();
    }
  };

  // While saving the fields stay focusable (a disabled input would drop focus to the page).
  const lockProps = saving ? { readOnly: true, 'aria-disabled': 'true' } : {};
  const limits = getDateLimits();
  const minDate = isoDay(limits.earliest);
  const maxDate = isoDay(limits.latest);

  return (
    <Section title="Time away" id="perf-away">
      <Card>
        <Box role="status" aria-live="polite" data-testid="away-live-region" sx={visuallyHidden}>
          {notice}
        </Box>
        <Typography sx={{ fontSize: 14, opacity: 0.85 }}>
          Tell us about vacations or injuries so time away does not look like missing data. Weeks
          you mark as away are left out of your consistency and trend calculations.
        </Typography>

        {userPeriods.length === 0 && (
          <Typography ref={listHeading} tabIndex={-1} sx={{ mt: 2, fontSize: 14 }}>
            {inferred.length === 0
              ? 'No away periods yet. Add one below when you plan time off or are recovering.'
              : 'You have not added any away periods yet.'}
          </Typography>
        )}

        {userPeriods.length > 0 && (
          <Typography
            variant="h3"
            ref={listHeading}
            tabIndex={-1}
            sx={{ mt: 2, fontSize: 16, fontWeight: 700 }}
          >
            Your away periods
          </Typography>
        )}
        {userPeriods.length > 0 && (
          <Box component="ul" aria-label="Your away periods" sx={{ p: 0, m: 0, mt: 2 }}>
            {userPeriods.map((p) => (
              <Box
                component="li"
                key={p.id}
                sx={{ listStyle: 'none', py: 1, borderTop: `1px solid ${COLORS.border}` }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    data-testid="away-period-label"
                    sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                  >
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {p.label || DEFAULT_LABEL}
                    </Box>
                    <Box component="span" sx={{ ml: 1, fontSize: 14, opacity: 0.8 }}>
                      {formatRange(p.startDate, p.endDate)}
                    </Box>
                  </Box>
                  {confirmId !== p.id && (
                    <Button
                      size="small"
                      variant="outlined"
                      ref={(el) => {
                        deleteButtons.current[p.id] = el;
                      }}
                      onClick={() => {
                        pendingFocus.current = () => yesButton.current;
                        setConfirmId(p.id);
                        setListError('');
                      }}
                      aria-label={`Delete away period ${p.label || DEFAULT_LABEL}, ${formatRange(
                        p.startDate,
                        p.endDate
                      )}`}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
                {confirmId === p.id && (
                  <Box
                    role="group"
                    aria-label={`Confirm deleting ${p.label || DEFAULT_LABEL}`}
                    sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Typography sx={{ fontSize: 14 }}>Delete this away period?</Typography>
                    <Button
                      size="small"
                      variant="contained"
                      ref={yesButton}
                      disabled={deletingId === p.id}
                      onClick={() => confirmDelete(p)}
                    >
                      Yes, delete
                    </Button>
                    <Button
                      size="small"
                      disabled={deletingId === p.id}
                      onClick={() => {
                        pendingFocus.current = () => deleteButtons.current[p.id];
                        setConfirmId(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
        {listError && (
          <Typography role="alert" sx={{ mt: 1, fontSize: 14, color: COLORS.caution }}>
            {listError}
          </Typography>
        )}

        {inferred.length > 0 && (
          <Box component="ul" aria-label="Breaks with no sessions" sx={{ p: 0, m: 0, mt: 2 }}>
            {inferred.map((p) => (
              <Box
                component="li"
                key={p.id}
                sx={{
                  listStyle: 'none',
                  py: 1,
                  borderTop: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Break (no sessions)
                  </Box>
                  <Box component="span" sx={{ ml: 1, fontSize: 14, opacity: 0.8 }}>
                    {p.ongoing ? `${p.startDate} to now` : formatRange(p.startDate, p.endDate)}
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => prefill(p)}
                  aria-label={`Add as away period: break ${formatRange(p.startDate, p.endDate)}`}
                >
                  Add as away period
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Typography id="away-help" sx={{ mt: 3, fontSize: 13, opacity: 0.8 }}>
          Dates are in UTC. You can go back up to 3 years or ahead 1 year, and a period can last up
          to {AWAY_MAX_DAYS} days.
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={submit}
          aria-label="Add an away period"
          sx={{
            mt: 1.5,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', tl: 'repeat(3, 1fr)' },
          }}
        >
          <TextField
            id="away-start"
            type="date"
            label="Start date"
            size="small"
            value={form.startDate}
            onChange={setField('startDate')}
            inputProps={{ ...lockProps, min: minDate, max: maxDate }}
            error={Boolean(errors.startDate)}
            helperText={errors.startDate}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="away-end"
            type="date"
            label="End date"
            size="small"
            value={form.endDate}
            onChange={setField('endDate')}
            inputProps={{ ...lockProps, min: minDate, max: maxDate }}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="away-label"
            label="Label"
            size="small"
            value={form.label}
            onChange={setField('label')}
            inputProps={lockProps}
            error={Boolean(errors.label)}
            helperText={errors.label || `Up to ${AWAY_LABEL_MAX} characters`}
          />
          {formError && (
            <Typography
              role="alert"
              sx={{ gridColumn: '1 / -1', fontSize: 14, color: COLORS.caution }}
            >
              {formError}
            </Typography>
          )}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Add away period'}
            </Button>
          </Box>
        </Box>
      </Card>
    </Section>
  );
};

export default AwayPeriods;
